
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {AccountUpdate2Response} from './AccountUpdate2Response';
import {AccountUpdateRequestParams} from './AccountUpdateRequestParams';
import {FieldModelAccountUpdateRequestParams} from './AccountUpdateRequestParams';

/**
 * AccountUpdate2Request struct
 */
class AccountUpdate2Request extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!AccountUpdateRequestParams=} accountParams
   * @constructor
   */
  constructor (parent = new Request(), accountParams = new AccountUpdateRequestParams()) {
    super()
    super.copy(parent)
    this.AccountParams = accountParams
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountUpdate2Request}
   * @param {!AccountUpdate2Request} other Other struct
   * @returns {!AccountUpdate2Request} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.AccountParams != null) {
      this.AccountParams = AccountUpdateRequestParams.fromObject(other.AccountParams)
    } else {
      this.AccountParams = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountUpdate2Request}
   * @returns {!AccountUpdate2Request} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountUpdate2RequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountUpdate2RequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountUpdate2Request}
   * @param {!AccountUpdate2Request} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountUpdate2Request)) {
      throw new TypeError('Instance of AccountUpdate2Request is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountUpdate2Request}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      AccountParams: ((this.AccountParams != null) ? this.AccountParams : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountUpdate2Request.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountUpdate2Request} other Object value
   * @returns {!AccountUpdate2Request} Created struct
   */
  static fromObject (other) {
    return new AccountUpdate2Request().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountUpdate2Request}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountUpdate2Request.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountUpdate2Request}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4091
  }
}

export { AccountUpdate2Request };

/**
 * Fast Binary Encoding AccountUpdate2Request field model
 */
class FieldModelAccountUpdate2Request extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.AccountParams = new FieldModelAccountUpdateRequestParams(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.AccountParams.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.AccountParams.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountUpdate2Request.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4091
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountParams.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountParams.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountParams.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!AccountUpdate2Request} fbeValue Default value, defaults is new AccountUpdate2Request()
   * @returns {!AccountUpdate2Request} AccountUpdate2Request value
   */
  get (fbeValue = new AccountUpdate2Request()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!AccountUpdate2Request} fbeValue AccountUpdate2Request value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountParams.fbeSize) <= fbeStructSize) {
      fbeValue.AccountParams = this.AccountParams.get()
    } else {
      fbeValue.AccountParams = new AccountUpdateRequestParams()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountParams.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountUpdate2Request}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountUpdate2Request}
   * @param {!AccountUpdate2Request} fbeValue AccountUpdate2Request value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountUpdate2Request}
   * @param {AccountUpdate2Request} fbeValue AccountUpdate2Request value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.AccountParams.set(fbeValue.AccountParams)
  }
}

export { FieldModelAccountUpdate2Request };

/**
 * Fast Binary Encoding AccountUpdate2Request model
 */
class AccountUpdate2RequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountUpdate2Request(this.buffer, 4)
  }

  /**
   * Get the AccountUpdate2Request model
   * @this {!AccountUpdate2RequestModel}
   * @returns {!FieldModelAccountUpdate2Request} model AccountUpdate2Request model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountUpdate2RequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountUpdate2RequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountUpdate2RequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountUpdate2RequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountUpdate2Request.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountUpdate2RequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountUpdate2RequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountUpdate2RequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountUpdate2RequestModel}
   * @param {!AccountUpdate2Request} value AccountUpdate2Request value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountUpdate2RequestModel}
   * @param {!AccountUpdate2Request} value AccountUpdate2Request value, defaults is new AccountUpdate2Request()
   * @return {!object} Deserialized AccountUpdate2Request value and its size
   */
  deserialize (value = new AccountUpdate2Request()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountUpdate2Request(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountUpdate2Request(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountUpdate2RequestModel}
   * @param {!number} prev Previous AccountUpdate2Request model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountUpdate2RequestModel };
AccountUpdate2Request.__has_response = true;
AccountUpdate2Request.__response_class = AccountUpdate2Response;
